import { FC, useCallback } from 'react';
import {
  ArrayField,
  BooleanField,
  CloneButton,
  Datagrid,
  Labeled,
  List,
  ReferenceField,
  ReferenceInput,
  RowClickFunction,
  SearchInput,
  SelectInput,
  TextField,
  useCreatePath,
  useRecordContext,
  useTranslate,
} from 'react-admin';

import { FormattedDateField } from 'components/FormattedDateField';
import { usePermission } from 'providers/auth/permissionProvider';
import { ReleaseSoftwareComponent } from 'types';

const softwareReleaseFilters = [
  <SearchInput key="search" source="q" alwaysOn />,
  <ReferenceInput key="hardware" source="hardware_id" reference="hardware" alwaysOn>
    <SelectInput source="name" />
  </ReferenceInput>,
];

const CloneRelease = () => {
  const record = useRecordContext();
  const clearName = ({ name, ...rest }: any) => rest;

  return record ? <CloneButton record={clearName(record)} /> : null;
};

const SoftwareComponentExpand = () => {
  const translate = useTranslate();
  const createPath = useCreatePath();
  const { hasPermission } = usePermission();
  // The software_components show page is registered unconditionally, but its menu entry is gated on
  // `dev_options` (src/resources/software-components/index.ts), so gate the row link the same way.
  const canShowSoftwareComponent = hasPermission('dev_options');

  // Without an explicit rowClick the nested Datagrid inherits the *outer* ResourceContext
  // (`software_releases`) and falls back to that resource's show page, which is why clicking a
  // component used to open an unrelated release. Resolve the path ourselves instead.
  const componentRowClick: RowClickFunction = useCallback(
    (_id, _resource, record) => {
      const { id } = record as ReleaseSoftwareComponent;

      // A release entry is built from its software *versions*, so a version whose component row is
      // gone arrives with only `software_version_id` and cannot be linked anywhere.
      if (id == null) {
        return false;
      }

      return createPath({ resource: 'software_components', type: 'show', id });
    },
    [createPath],
  );

  return (
    <Labeled
      label={translate('resources.software_components.name', {
        smart_count: 2,
      })}
      fullWidth
    >
      <ArrayField source="software_components">
        <Datagrid
          bulkActionButtons={false}
          rowClick={canShowSoftwareComponent ? componentRowClick : false}
          hover={canShowSoftwareComponent}
        >
          <TextField source="name" emptyText="-" />
          <ReferenceField source="software_version_id" reference="software_versions">
            <TextField source="name" />
          </ReferenceField>
        </Datagrid>
      </ArrayField>
    </Labeled>
  );
};

export const SoftwareReleaseList: FC = () => {
  return (
    <List filters={softwareReleaseFilters} sort={{ field: 'created_at', order: 'DESC' }}>
      <Datagrid rowClick="show" bulkActionButtons={false} expand={<SoftwareComponentExpand />} expandSingle>
        <TextField source="name" />
        <ReferenceField source="hardware_id" reference="hardware" emptyText="-">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField source="hardware_revision_id" reference="hardware_revisions" emptyText="-">
          <TextField source="name" />
        </ReferenceField>
        <BooleanField source="custom" />
        <FormattedDateField source="created_at" showTime />
        <CloneRelease />
      </Datagrid>
    </List>
  );
};
